import getYouTubeId from "get-youtube-id";
import NextImage from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { IoMdPlay } from "react-icons/io";
import Player from "react-player";

export const YoutubePlayer = ({
  url,
  previewvideourl,
}: {
  url: string;
  previewvideourl?: string;
}) => {
  const [playing, setPlaying] = useState(false);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const videoId = getYouTubeId(url);
  const [thumbnailUrl, setThumbnailUrl] = useState("");

  const sanitizedUrl = url.replace("youtube.com", "youtube-nocookie.com");
  const sanitizedPreviewvideoUrl = previewvideourl?.replace("youtube.com", "youtube-nocookie.com");

  // disallow the player from rendering in SSR mode since it blows up
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  //   fetch highest res thumbnail possible
  useEffect(() => {
    if (!previewvideourl) {
      const maxresThumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
      const sdThumbnailUrl = `https://img.youtube.com/vi/${videoId}/sddefault.jpg`;
      const image = new Image();
      image.onload = () => {
        if (image.width === 120 && image.height === 90) {
          // These dimensions mean it's the placeholder image
          setThumbnailUrl(sdThumbnailUrl);
        } else {
          setThumbnailUrl(maxresThumbnailUrl);
        }
      };
      image.onerror = () => {
        setThumbnailUrl(sdThumbnailUrl); // Fallback if any error occurs
      };
      image.src = maxresThumbnailUrl;
    }
  }, [previewvideourl, videoId]);

  const handlePlay = useCallback(() => {
    setPlaying(true);
    if (overlayRef.current) {
      overlayRef.current.classList.add("opacity-0"); // Start the fade-out
      setTimeout(() => {
        overlayRef.current?.classList.add("hidden"); // Completely hide after fade-out
      }, 1000); // This duration should match the CSS transition duration
    }
  }, []);

  return (
    mounted &&
    videoId && (
      <div className="relative" onClick={handlePlay}>
        <div className="block aspect-video h-auto">
          <Player
            id={videoId}
            playing={playing}
            playsinline
            light={!playing}
            className={"block !aspect-video !h-auto"}
            url={sanitizedUrl}
            width={"100%"}
            controls={true}
          />
        </div>
        <div
          ref={overlayRef}
          className="group absolute inset-0 h-[100.1%] w-[100.1%] cursor-pointer bg-black transition-opacity duration-1000"
          onClick={handlePlay}
        >
          {sanitizedPreviewvideoUrl ? (
            <>
              <Player
                url={sanitizedPreviewvideoUrl}
                className="absolute block"
                width={"100%"}
                height={"100%"}
                controls={false}
                loop={true}
                muted={true}
                playsinline
                playing={true}
                onReady={(e) => {
                  e.getInternalPlayer().disableRemotePlayback = true;
                }}
              />
              <div
                className="absolute h-[100.1%] w-[100.1%]"
                style={{
                  backgroundImage: `
      linear-gradient(240deg, rgba(2, 2, 2, 0.1) 0%, rgba(2, 2, 2, 0.05) 40%), 
      linear-gradient(0deg, rgba(2, 2, 2, 0.2) 0%, rgba(2, 2, 2, 0.05) 40%), 
      linear-gradient(120deg, rgba(2, 2, 2, 0.4) 0%, rgba(2, 2, 2, 0.05) 60%), 
      linear-gradient(180deg, rgba(2, 2, 2, 0.2) 0%, rgba(2, 2, 2, 0.05) 30%)`,
                }}
              />
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
                <IoMdPlay
                  size={50}
                  color="#fffff2"
                  className="hidden scale-110 pl-0.5 transition-all duration-200 animate-in fade-in group-hover:block group-hover:!text-white"
                />
              </div>
            </>
          ) : (
            thumbnailUrl && (
              <>
                <NextImage
                  src={thumbnailUrl}
                  fill={true}
                  alt=""
                  // sizes="(max-width: 768px) 100vw, (max-width: 1200px) 75vw, 50vw"
                  className="absolute object-cover object-center"
                />
                <div
                  className="absolute"
                  style={{
                    backgroundImage: `
              linear-gradient(240deg, rgba(2, 2, 2, 0.3) 0%, rgba(2, 2, 2, 0.02) 40%), 
              linear-gradient(0deg, rgba(2, 2, 2, 0.5) 0%, rgba(2, 2, 2, 0.02) 40%), 
              linear-gradient(120deg, rgba(2, 2, 2, 0.4) 0%, rgba(2, 2, 2, 0.02) 60%), 
              linear-gradient(180deg, rgba(2, 2, 2, 0) 0%, rgba(2, 2, 2, 0.02) 30%)`,
                  }}
                />
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
                  <IoMdPlay
                    size={50}
                    color="#fffff2"
                    className="pl-0.5 transition-all duration-200 group-hover:scale-110 group-hover:!text-white"
                  />
                </div>
              </>
            )
          )}
        </div>
      </div>
    )
  );
};
